<script lang="ts" setup>
import RouterVerticalTab from '@/components/router-vertical-tabs/router-vertical-tab.vue';
import RouterVerticalTabs from '@/components/router-vertical-tabs/router-vertical-tabs.vue';
import {
  GetAttributeOptionsResponseItem,
  ProjectModel,
  WeeekDataModel,
  WeeekOptionsGetPrioritiesRequest,
  WeeekOptionsGetStatusesRequest,
  WeeekOptionsGetTagsRequest,
  WeeekOptionsGetTypesRequest,
} from '@/models';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import {
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  QueueListIcon,
  TagIcon,
  UserIcon,
} from 'tir-style-system/icons/outline';
import { computed, inject, onBeforeMount, onMounted, ref, Ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import WeeekOptionsGetProjectOptionsRequest from '../project-settings-weeek-page/models/WeeekOptionsGetProjectOptionsRequest';
import { AdapterService } from './adapter';
import { WeeekTabsEnum } from '../project-settings-weeek-page/enums';
import { TagModel } from '@/components/task-forms/models';
import { GetTagsListDetailRequest } from '@/components/tasks-kanban/components/options-filter/components/filter-parameter/adapter/models';
import { debounce } from 'tir-pm-debounce';

/**Маршруты */
const router = useRouter();
/**Адаптер */
const adapter = new AdapterService();

/**Параметры */
defineProps({
  /**Доступ к weeek */
  isAccessWeeek: { type: Boolean },
});
/**События */
const emit = defineEmits<{
  checkRightsFormQueue: [userData: boolean];
  /**Обновление проекта */
  uppdateProject: [];
  /**Обновление разрешения для импорта с weeek */
  updateAllowImportWeeek: [state: boolean];
}>();

/**Информация о проекте */
const project = inject('project') as Ref<ProjectModel>;

/**id проекта */
const projectId = router.currentRoute.value.params.projectId;

/**api key */
const apiKey = ref();
/**Данные weeek */
const dataWeeek = ref<WeeekDataModel>({
  StatusesWeeek: [],
  PrioritiesWeeek: [],
  TypesWeeek: [],
  TagsWeeek: [],
});
/**Изначальные данные weeek */
const originalDataWeeek = ref<WeeekDataModel>({
  StatusesWeeek: [],
  PrioritiesWeeek: [],
  TypesWeeek: [],
  TagsWeeek: [],
});
/**Теги проекта ПМ */
const tagsProjectPm = ref<TagModel[]>([]);
/**Состояние weeek */
const weeekState = ref<boolean>();

/**Изменения в массиве weeek*/
const changeDataWeeek = computed(
  () => JSON.stringify(originalDataWeeek.value) !== JSON.stringify(dataWeeek.value),
);
/**Привязан ли токен */
const isTokenLinked = computed(() => !!apiKey.value);
/**Владелец */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isProjectAdmin = computed(() => !!project.value.Admins?.some((a) => a.Id == CurrentUser.Id));
/**Классы элемента с контентом */
const contentClasses = computed(() => ({ form: router.currentRoute.value.name == 'project-tags' }));
/**Подзаголовок настройки weeek*/
const subtitleWeeekSettings = computed(() =>
  isTokenLinked.value ? 'Токен привязан' : 'Токен не привязан',
);

/**Отслеживание изменения привязки токена */
watch(
  isTokenLinked,
  async () => {
    if (isTokenLinked.value) {
      await getAttributesWeeek();
    }
  },
  { immediate: true },
);

/**Перед рендером */
onBeforeMount(async () => {
  if (!isOwner.value && !isProjectAdmin.value) {
    const route = String(router.currentRoute.value.name);
    if (route == 'project-tags') {
      router.push('/');
    }
  }
});

/**После рендера  */
onMounted(async () => {
  await getSettingsProject();
});

/**Обновление api key */
const updateApiKey = (_value: string) => {
  apiKey.value = _value;
};
/** Получение настроек проекта*/
const getSettingsProject = async () => {
  const response = await adapter.getProjectOptionsAsync(
    new WeeekOptionsGetProjectOptionsRequest({
      ProjectId: Number(projectId),
    }),
  );
  if (response.IsSuccess) {
    apiKey.value = response.Value.ApiKey;
    weeekState.value = response.Value.WeeekEnable;
  }
};
/**Обновление оригинального массива статусов weeek */
const updateOriginalDataWeeek = () => {
  originalDataWeeek.value = JSON.parse(JSON.stringify(dataWeeek.value));
};
/**Получение сопоставления cтатусов weeek */
const getStatusesWeeek = async () => {
  const response = await adapter.getStatusesWeeek(
    new WeeekOptionsGetStatusesRequest({
      ProjectId: Number(projectId),
    }),
  );
  if (response.IsSuccess) {
    dataWeeek.value.StatusesWeeek = response.Value.Attributes;
    originalDataWeeek.value.StatusesWeeek = JSON.parse(JSON.stringify(response.Value.Attributes));
  }
};
/**Получение сопоставления приоритетов weeek */
const getPrioritiesWeeek = async () => {
  const response = await adapter.getPrioritiesWeeek(
    new WeeekOptionsGetPrioritiesRequest({
      ProjectId: Number(projectId),
    }),
  );
  if (response.IsSuccess) {
    dataWeeek.value.PrioritiesWeeek = response.Value.Attributes;
    originalDataWeeek.value.PrioritiesWeeek = JSON.parse(JSON.stringify(response.Value.Attributes));
  }
};
/**Получение сопоставления типов weeek */
const getTypesWeeek = async () => {
  const response = await adapter.getTypesAsync(
    new WeeekOptionsGetTypesRequest({
      ProjectId: Number(projectId),
    }),
  );
  if (response.IsSuccess) {
    dataWeeek.value.TypesWeeek = response.Value.Attributes;
    originalDataWeeek.value.TypesWeeek = JSON.parse(JSON.stringify(response.Value.Attributes));
  }
};
/**Получение сопоставления тегов weeek */
const getTagsWeeek = async () => {
  const response = await adapter.getTagsAsync(
    new WeeekOptionsGetTagsRequest({
      ProjectId: Number(projectId),
    }),
  );
  if (response.IsSuccess) {
    dataWeeek.value.TagsWeeek = response.Value.Attributes;
    originalDataWeeek.value.TagsWeeek = JSON.parse(JSON.stringify(response.Value.Attributes));
  }
};
/**Получить теги проекта */
const getTagsProject = async (_searchText?: string) => {
  const responce = await adapter.getTagsProjectPm(
    new GetTagsListDetailRequest({
      Search: _searchText,
      ProjectId: Number(projectId),
    }),
  );
  if (responce.IsSuccess) {
    tagsProjectPm.value = responce.Value.Items.map(
      (tag) =>
        new TagModel({
          Id: tag.Id,
          Color: tag.Color,
          Description: tag.Description,
          Name: tag.TagName,
        }),
    );
  }
};
/**Обновить состояние weeek */
const updateAllowImportWeeek = (state: boolean) => {
  emit('updateAllowImportWeeek', state);
};
/**Обновить данные проекта */
const uppdateProject = async () => {
  emit('uppdateProject');
  await getSettingsProject();
};
const searchTags = debounce((searchText: string) => getTagsProject(searchText), 300);
/**Получить атрибуты weeek */
const getAttributesWeeek = async () => {
  await getStatusesWeeek();
  await getPrioritiesWeeek();
  await getTypesWeeek();
  await getTagsWeeek();
  await getTagsProject();
};
/**Обновить список атрибутов weeek*/
const updateAttributesWeeek = async () => {
  const nameRoute = router.currentRoute.value.name;
  switch (nameRoute) {
    case WeeekTabsEnum.Statuses:
      await getStatusesWeeek();
      break;
    case WeeekTabsEnum.Priorities:
      await getPrioritiesWeeek();
      break;
    case WeeekTabsEnum.Types:
      await getTypesWeeek();
      break;
    case WeeekTabsEnum.Tags:
      await getTagsWeeek();
      break;
  }
};
</script>

<template>
  <div class="project-page">
    <div class="project-page__side-menu" v-if="isOwner || isProjectAdmin">
      <RouterVerticalTabs>
        <template #default="{ currentTab }">
          <RouterVerticalTab
            :route="{ name: 'project-namespaces' }"
            label="Обзор проекта"
            :current-tab="currentTab"
            v-if="isOwner || isProjectAdmin"
          >
            <template #left>
              <QueueListIcon />
            </template>
          </RouterVerticalTab>
          <RouterVerticalTab
            :route="{ name: 'project-settings' }"
            label="Основная информация"
            :current-tab="currentTab"
            v-if="isOwner || isProjectAdmin"
          >
            <template #left>
              <Cog6ToothIcon />
            </template>
          </RouterVerticalTab>
          <RouterVerticalTab
            :route="{ name: 'project-admins' }"
            label="Администраторы"
            :current-tab="currentTab"
            v-if="isOwner || isProjectAdmin"
          >
            <template #left> <UserIcon /> </template>
          </RouterVerticalTab>
          <RouterVerticalTab
            :route="{ name: 'project-tags' }"
            label="Теги"
            :current-tab="currentTab"
            v-if="isOwner || isProjectAdmin"
          >
            <template #left> <TagIcon /> </template>
          </RouterVerticalTab>
          <RouterVerticalTab
            v-if="weeekState && (isOwner || isProjectAdmin)"
            class="project-page__router-import-weeek"
            :route="{ name: 'import-weeek' }"
            label="Настройка импорта Weeek"
            paragraph="Токен не привязан"
            :current-tab="currentTab"
          >
            <template #left> <ArrowDownTrayIcon /> </template>
            <template #paragraph>
              <div
                class="project-page__router-paragraph"
                :class="{ 'project-page__token-linked-success': isTokenLinked }"
              >
                {{ subtitleWeeekSettings }}
              </div>
            </template>
          </RouterVerticalTab>
        </template>
      </RouterVerticalTabs>
    </div>
    <div class="project-page__content" :class="contentClasses">
      <RouterView
        :projectId="projectId"
        :apiKey="apiKey"
        :isAccessWeeek="isAccessWeeek"
        :isTokenLinked="isTokenLinked"
        :dataWeeek="dataWeeek"
        :changeDataWeeek="changeDataWeeek"
        :tagsProjectPm="tagsProjectPm"
        @searchTags="searchTags"
        @checkRightsFormQueue="emit('checkRightsFormQueue', $event)"
        @uppdateProject="uppdateProject"
        @updateAllowImportWeeek="updateAllowImportWeeek"
        @updateApiKey="updateApiKey"
        @updateProjectSettings="getSettingsProject"
        @updateOriginalDataWeeek="updateOriginalDataWeeek"
        @updateList="updateAttributesWeeek"
      />
    </div>
  </div>
</template>

<style lang="scss">
.project-page {
  display: flex;
  gap: 0.75rem;
  min-height: calc(100vh - 10.5rem);

  &__router-import-weeek {
    align-items: flex-start !important;
  }

  &__side-menu,
  &__content {
    border-radius: 0.5rem;
    background: var(--neutral-0, #fff);
    box-shadow: 0rem 0.25rem 0.5rem 0rem rgba(0, 0, 0, 0.04),
      0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.04);
  }
  &__router-paragraph {
    font-size: 0.875rem;
    color: var(--text-negative, #ec2e14);
  }
  &__side-menu {
    width: 17.5rem;
    overflow: hidden;
  }

  &__token-linked-success {
    color: var(--text-info, #2a77ef);
  }

  &__content {
    padding: 2.25rem 1.25rem 0 1.25rem;
    flex: 1;
    &.form {
      padding: 0.75rem;
      .template-import-form_sidebar {
        right: calc(100% + 1.5rem);
        border: none;
      }
    }
  }
}
</style>
